import { useEffect, useState } from 'react';

/**
 * iOS Safari doesn't shrink the layout viewport (what `position: fixed` pins
 * to) when the on-screen keyboard opens — only `window.visualViewport` does.
 * This tracks the gap between the two so a fixed-to-bottom sheet can offset
 * itself above the keyboard instead of sitting under it. Returns 0 (a no-op)
 * on browsers that already resize the layout viewport for the keyboard.
 */
function useVisualViewportKeyboard() {
  const [state, setState] = useState({ inset: 0, offsetTop: 0 });

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }

    const update = () => {
      const inset = Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop));
      const offsetTop = Math.max(0, Math.round(viewport.offsetTop));
      setState((prev) => (prev.inset === inset && prev.offsetTop === offsetTop ? prev : { inset, offsetTop }));
    };

    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, []);

  return state;
}

export function useKeyboardInset() {
  return useVisualViewportKeyboard().inset;
}

/**
 * The companion half of `useKeyboardInset`: how far the VISUAL viewport has
 * panned down from the LAYOUT viewport's top (e.g. iOS Safari scrolling the
 * page to keep a focused field above the keyboard). A `position: fixed`
 * element is pinned to the layout viewport, so once this is non-zero it is
 * scrolled out of the visible (visual-viewport) window by this many px —
 * apply it as a counter-`translateY` to keep a fixed sheet's top edge (e.g.
 * a header) on screen.
 */
export function useKeyboardPanOffset() {
  return useVisualViewportKeyboard().offsetTop;
}
